import { useId, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'
import {
  Button, Input, Label, Textarea,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@agion/ui'
import type { CloudProvider } from '@/core/infra_types'
import { createCredential, credentialTypesByProvider } from './infra_credential.service'
import type { CreateCredentialRequest } from './infra_credential.service'

interface Props {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const cloudProviders: { value: CloudProvider; label: string }[] = [
  { value: 'gcp', label: 'Google Cloud Platform' },
  { value: 'azure', label: 'Microsoft Azure' },
  { value: 'aws', label: 'Amazon Web Services' },
  { value: 'upcloud', label: 'UpCloud' },
]

// Format hints — not user-facing prose. Same across all locales by design.
const UUID_PLACEHOLDER = 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
const UPCLOUD_TOKEN_PLACEHOLDER = 'ucat_...'
const JSON_FILE_MAX_BYTES = 64 * 1024
const EMPTY_AZURE_FIELDS = { tenantId: '', subscriptionId: '', clientId: '', clientSecret: '' }
const EMPTY_UPCLOUD_FIELDS = { token: '', username: '', password: '' }

export function AddCredentialDialog({ open, onClose, onSuccess }: Props) {
  const { t } = useTranslation(['pages'])
  const jsonFileInputId = useId()
  const [name, setName] = useState('')
  const [provider, setProvider] = useState<CloudProvider | ''>('')
  const [credType, setCredType] = useState('')
  const [secretInput, setSecretInput] = useState('')
  const [azureFields, setAzureFields] = useState(EMPTY_AZURE_FIELDS)
  const [upcloudFields, setUpcloudFields] = useState(EMPTY_UPCLOUD_FIELDS)
  const [upcloudAuthMode, setUpcloudAuthMode] = useState<'token' | 'password'>('token')
  const [jsonImportError, setJsonImportError] = useState<string | null>(null)
  const [jsonImportStatus, setJsonImportStatus] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const credTypes = provider ? credentialTypesByProvider[provider] : []
  const supportsJsonImport = provider
    ? isJsonFileImportSupported(provider, credType)
    : false

  const resetJsonImportState = () => {
    setJsonImportError(null)
    setJsonImportStatus(null)
  }

  const handleJsonFileImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || !provider) return

    resetJsonImportState()
    if (file.size > JSON_FILE_MAX_BYTES) {
      setJsonImportError(t('pages:credentials.addDialog.jsonImport.tooLarge', 'Choose a JSON file smaller than 64 KB.'))
      return
    }

    try {
      const fileText = await readTextFile(file)
      const parsed = parseJsonCredentialFile(fileText)

      if (provider === 'gcp' && credType === 'service_account') {
        if (readStringField(parsed, ['type']) !== 'service_account') {
          setJsonImportError(t('pages:credentials.addDialog.jsonImport.invalidGcp', 'Choose a GCP service account JSON file.'))
          return
        }
        setSecretInput(fileText.trim())
        setJsonImportStatus(t('pages:credentials.addDialog.jsonImport.imported', 'JSON imported.'))
        return
      }

      if (provider === 'azure' && credType === 'service_principal') {
        const importedAzureFields = mapAzureServicePrincipalJson(parsed)
        if (!importedAzureFields.tenantId || !importedAzureFields.clientId || !importedAzureFields.clientSecret) {
          setJsonImportError(t('pages:credentials.addDialog.jsonImport.invalidAzure', 'Choose an Azure service principal JSON file.'))
          return
        }
        setAzureFields(importedAzureFields)
        setJsonImportStatus(t('pages:credentials.addDialog.jsonImport.imported', 'JSON imported.'))
      }
    } catch {
      setJsonImportError(t('pages:credentials.addDialog.jsonImport.invalidJson', 'Choose a valid JSON file.'))
    }
  }

  const handleSubmit = async () => {
    if (!name || !provider || !credType) return
    // Validate provider-specific fields
    if (provider === 'azure' && credType === 'service_principal') {
      if (!azureFields.tenantId || !azureFields.subscriptionId || !azureFields.clientId || !azureFields.clientSecret) return
    } else if (provider === 'upcloud' && credType === 'api_token') {
      if (upcloudAuthMode === 'token' ? !upcloudFields.token : (!upcloudFields.username || !upcloudFields.password)) return
    } else if (!secretInput) {
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      const secretData = provider === 'azure' && credType === 'service_principal'
        ? { tenant_id: azureFields.tenantId, subscription_id: azureFields.subscriptionId, client_id: azureFields.clientId, client_secret: azureFields.clientSecret }
        : provider === 'upcloud' && credType === 'api_token'
          ? (upcloudAuthMode === 'token'
            ? { token: upcloudFields.token }
            : { username: upcloudFields.username, password: upcloudFields.password })
          : buildSecretData(provider, credType, secretInput)
      const metadata = provider === 'azure' && credType === 'service_principal'
        ? { tenant_id: azureFields.tenantId, subscription_id: azureFields.subscriptionId }
        : buildMetadata(provider, secretInput)

      const req: CreateCredentialRequest = {
        name,
        cloudProvider: provider,
        credentialType: credType,
        secretData,
        metadata,
      }
      await createCredential(req)
      onSuccess()
      handleClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create credential')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setName('')
    setProvider('')
    setCredType('')
    setSecretInput('')
    setAzureFields(EMPTY_AZURE_FIELDS)
    setUpcloudFields(EMPTY_UPCLOUD_FIELDS)
    setUpcloudAuthMode('token')
    resetJsonImportState()
    setError(null)
    onClose()
  }

  const hasSecretInput = provider === 'azure' && credType === 'service_principal'
    ? azureFields.tenantId && azureFields.subscriptionId && azureFields.clientId && azureFields.clientSecret
    : provider === 'upcloud' && credType === 'api_token'
      ? (upcloudAuthMode === 'token' ? upcloudFields.token : upcloudFields.username && upcloudFields.password)
      : secretInput
  const canSubmit = name && provider && credType && hasSecretInput && !isSubmitting

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('pages:credentials.addDialog.title', 'Add Cloud Credential')}
          </DialogTitle>
          <DialogDescription>
            {t('pages:credentials.addDialog.description', 'Store a cloud provider credential securely in OpenBao.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('pages:credentials.addDialog.name', 'Name')}</Label>
            <Input
              placeholder={t('pages:credentials.addDialog.namePlaceholder', 'e.g. production-gcp')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('pages:credentials.addDialog.provider', 'Cloud Provider')}</Label>
            <Select
              value={provider || undefined}
              onValueChange={(val) => {
                setProvider(val as CloudProvider)
                setCredType('')
                setSecretInput('')
                resetJsonImportState()
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('pages:credentials.addDialog.selectProvider', 'Select provider...')} />
              </SelectTrigger>
              <SelectContent>
                {cloudProviders.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {provider && (
            <div className="space-y-2">
              <Label>{t('pages:credentials.addDialog.type', 'Credential Type')}</Label>
              <Select
                value={credType || undefined}
                onValueChange={(value) => {
                  setCredType(value)
                  resetJsonImportState()
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('pages:credentials.addDialog.selectType', 'Select type...')} />
                </SelectTrigger>
                <SelectContent>
                  {credTypes.map((ct) => (
                    <SelectItem key={ct.value} value={ct.value}>
                      {ct.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {provider === 'azure' && credType === 'service_principal' && (
            <div className="space-y-3">
              {supportsJsonImport && (
                <JsonFileImportControl
                  inputId={jsonFileInputId}
                  onChange={handleJsonFileImport}
                  error={jsonImportError}
                  status={jsonImportStatus}
                />
              )}
              <div className="space-y-2">
                <Label>{t('pages:credentials.addDialog.azure.tenantId', 'Tenant ID')}</Label>
                <Input
                  className="font-mono text-xs"
                  placeholder={UUID_PLACEHOLDER}
                  value={azureFields.tenantId}
                  onChange={(e) => setAzureFields(prev => ({ ...prev, tenantId: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('pages:credentials.addDialog.azure.subscriptionId', 'Subscription ID')}</Label>
                <Input
                  className="font-mono text-xs"
                  placeholder={UUID_PLACEHOLDER}
                  value={azureFields.subscriptionId}
                  onChange={(e) => setAzureFields(prev => ({ ...prev, subscriptionId: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('pages:credentials.addDialog.azure.clientId', 'Client ID (App ID)')}</Label>
                <Input
                  className="font-mono text-xs"
                  placeholder={UUID_PLACEHOLDER}
                  value={azureFields.clientId}
                  onChange={(e) => setAzureFields(prev => ({ ...prev, clientId: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('pages:credentials.addDialog.azure.clientSecret', 'Client Secret')}</Label>
                <Input
                  type="password"
                  placeholder={t('pages:credentials.addDialog.azure.clientSecretPlaceholder', 'Client secret value')}
                  value={azureFields.clientSecret}
                  onChange={(e) => setAzureFields(prev => ({ ...prev, clientSecret: e.target.value }))}
                />
              </div>
              <p className="text-xs text-slate-400">
                {t('pages:credentials.addDialog.secretHint', 'This value will be stored securely in OpenBao and never displayed again.')}
              </p>
            </div>
          )}

          {provider === 'upcloud' && credType === 'api_token' && (
            <div className="space-y-3">
              {/* Auth mode toggle */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setUpcloudAuthMode('token')}
                  className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                    upcloudAuthMode === 'token'
                      ? 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400'
                      : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t('pages:credentials.addDialog.upcloud.tokenMode', 'API Token (Recommended)')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setUpcloudAuthMode('password')}
                  className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                    upcloudAuthMode === 'password'
                      ? 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400'
                      : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t('pages:credentials.addDialog.upcloud.passwordMode', 'Username / Password')}
                </Button>
              </div>

              {upcloudAuthMode === 'token' ? (
                <div className="space-y-2">
                  <Label>{t('pages:credentials.addDialog.upcloud.token', 'API Token')}</Label>
                  <Input
                    type="password"
                    className="font-mono text-xs"
                    placeholder={UPCLOUD_TOKEN_PLACEHOLDER}
                    value={upcloudFields.token}
                    onChange={(e) => setUpcloudFields(prev => ({ ...prev, token: e.target.value }))}
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>{t('pages:credentials.addDialog.upcloud.username', 'Username')}</Label>
                    <Input
                      placeholder={t('pages:credentials.addDialog.upcloud.usernamePlaceholder', 'UpCloud API username')}
                      value={upcloudFields.username}
                      onChange={(e) => setUpcloudFields(prev => ({ ...prev, username: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('pages:credentials.addDialog.upcloud.password', 'Password')}</Label>
                    <Input
                      type="password"
                      placeholder={t('pages:credentials.addDialog.upcloud.passwordPlaceholder', 'UpCloud API password')}
                      value={upcloudFields.password}
                      onChange={(e) => setUpcloudFields(prev => ({ ...prev, password: e.target.value }))}
                    />
                  </div>
                </>
              )}
              <p className="text-xs text-slate-400">
                {t('pages:credentials.addDialog.secretHint', 'This value will be stored securely in OpenBao and never displayed again.')}
              </p>
            </div>
          )}

          {provider && credType && !(provider === 'azure' && credType === 'service_principal') && !(provider === 'upcloud' && credType === 'api_token') && (
            <div className="space-y-3">
              {supportsJsonImport && (
                <JsonFileImportControl
                  inputId={jsonFileInputId}
                  onChange={handleJsonFileImport}
                  error={jsonImportError}
                  status={jsonImportStatus}
                />
              )}
              <div className="space-y-2">
                <Label>{getSecretLabel(provider, credType)}</Label>
                <Textarea
                  className="font-mono text-xs min-h-[120px]"
                  placeholder={getSecretPlaceholder(provider, credType)}
                  value={secretInput}
                  onChange={(e) => setSecretInput(e.target.value)}
                />
              </div>
              <p className="text-xs text-slate-400">
                {t('pages:credentials.addDialog.secretHint', 'This value will be stored securely in OpenBao and never displayed again.')}
              </p>
            </div>
          )}

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {t('pages:credentials.addDialog.cancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {isSubmitting
              ? t('pages:credentials.addDialog.saving', 'Saving...')
              : t('pages:credentials.addDialog.save', 'Save Credential')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface JsonFileImportControlProps {
  inputId: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  error: string | null
  status: string | null
}

function JsonFileImportControl({ inputId, onChange, error, status }: JsonFileImportControlProps) {
  const { t } = useTranslation(['pages'])

  return (
    <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-900/60">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {t('pages:credentials.addDialog.jsonImport.title', 'JSON file')}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('pages:credentials.addDialog.jsonImport.description', 'Parsed locally. Max 64 KB.')}
          </p>
        </div>
        <Label
          htmlFor={inputId}
          className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {t('pages:credentials.addDialog.jsonImport.button', 'Import JSON file')}
        </Label>
        <Input
          id={inputId}
          aria-label={t('pages:credentials.addDialog.jsonImport.button', 'Import JSON file')}
          className="sr-only"
          type="file"
          variant="inline"
          accept="application/json,.json"
          onChange={onChange}
        />
      </div>
      {status && <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">{status}</p>}
      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
    </div>
  )
}

function isJsonFileImportSupported(provider: CloudProvider, credType: string): boolean {
  return (
    (provider === 'gcp' && credType === 'service_account')
    || (provider === 'azure' && credType === 'service_principal')
  )
}

function getSecretLabel(provider: CloudProvider, credType: string): string {
  if (provider === 'gcp' && credType === 'service_account') return 'Service Account JSON'
  if (provider === 'azure' && credType === 'service_principal') return 'Service Principal Credentials'
  if (provider === 'aws' && credType === 'iam_role') return 'IAM Credentials'
  if (provider === 'upcloud' && credType === 'api_token') return 'API Credentials'
  return 'Secret Data'
}

function getSecretPlaceholder(provider: CloudProvider, credType: string): string {
  if (provider === 'gcp' && credType === 'service_account') {
    return 'Paste the full service account JSON key file contents...'
  }
  if (provider === 'azure' && credType === 'service_principal') {
    return 'tenant_id=...\nclient_id=...\nclient_secret=...'
  }
  if (provider === 'aws' && credType === 'iam_role') {
    return 'access_key_id=...\nsecret_access_key=...'
  }
  if (provider === 'upcloud' && credType === 'api_token') {
    return 'username=...\npassword=...'
  }
  return ''
}

function buildSecretData(provider: CloudProvider, credType: string, input: string): Record<string, string> {
  // GCP service account: store the entire JSON as a single key
  if (provider === 'gcp' && credType === 'service_account') {
    return { service_account_json: input.trim() }
  }

  // Key=value format for other providers
  const data: Record<string, string> = {}
  for (const line of input.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed || !trimmed.includes('=')) continue
    const eqIdx = trimmed.indexOf('=')
    const key = trimmed.substring(0, eqIdx).trim()
    const value = trimmed.substring(eqIdx + 1).trim()
    if (key && value) data[key] = value
  }
  return data
}

function parseJsonCredentialFile(input: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(input)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('JSON credential file must be an object')
  }
  return parsed as Record<string, unknown>
}

function readTextFile(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Failed to read JSON file'))
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
        return
      }
      reject(new Error('Failed to read JSON file as text'))
    }
    reader.readAsText(file)
  })
}

function mapAzureServicePrincipalJson(parsed: Record<string, unknown>) {
  return {
    tenantId: readStringField(parsed, ['tenantId', 'tenant']),
    subscriptionId: readStringField(parsed, ['subscriptionId', 'subscription_id']),
    clientId: readStringField(parsed, ['clientId', 'appId']),
    clientSecret: readStringField(parsed, ['clientSecret', 'password']),
  }
}

function readStringField(parsed: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = parsed[key]
    if (typeof value === 'string') return value.trim()
  }
  return ''
}

function buildMetadata(provider: CloudProvider, input: string): Record<string, unknown> {
  if (provider === 'gcp') {
    try {
      const parsed = JSON.parse(input.trim())
      return {
        project_id: parsed.project_id || '',
        service_account_email: parsed.client_email || '',
      }
    } catch {
      return {}
    }
  }

  // Parse key=value to extract metadata
  const kv: Record<string, string> = {}
  for (const line of input.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed || !trimmed.includes('=')) continue
    const eqIdx = trimmed.indexOf('=')
    kv[trimmed.substring(0, eqIdx).trim()] = trimmed.substring(eqIdx + 1).trim()
  }

  if (provider === 'azure') {
    return {
      tenant_id: kv['tenant_id'] || '',
      subscription_id: kv['subscription_id'] || '',
    }
  }

  return {}
}
